import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  createManualSession,
  createManualWeek,
  updatePlannedSessionDate,
  updateSessionStatus,
  updateWorkoutDate,
  type CalendarSession,
  type WriteResponse,
} from '@/lib/api';
import { invalidateCalendarQueries } from './calendarQueryInvalidation';

/**
 * Hook for creating a single planned session.
 * Automatically invalidates calendar queries on success.
 */
export function useCreatePlannedSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createManualSession,
    onSuccess: () => {
      invalidateCalendarQueries(queryClient);
    },
  });
}

/**
 * Hook for creating a planned week (multiple sessions).
 * Automatically invalidates calendar queries on success.
 */
export function useCreatePlannedWeek() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ weekStart, sessions }: { weekStart: string; sessions: Array<{
      date: string;
      sport: 'Run' | 'Bike' | 'Swim' | 'Triathlon' | 'Crossfit' | 'Strength' | 'Walk';
      type: 'easy' | 'workout' | 'long' | 'rest' | 'threshold' | 'vo2' | 'tempo' | 'recovery' | 'race' | 'cross';
      distance_km?: number | null;
      duration_minutes?: number | null;
      notes?: string | null;
    }> }) => createManualWeek(weekStart, sessions),
    onSuccess: () => {
      invalidateCalendarQueries(queryClient);
    },
  });
}

/**
 * Hook for updating a planned session's date.
 * Automatically invalidates calendar queries on success.
 */
export function useUpdatePlannedSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      sessionId,
      scheduledDate,
      startTime,
      orderInDay,
    }: {
      sessionId: string;
      scheduledDate: string;
      startTime?: string | null;
      orderInDay?: number | null;
    }) => updatePlannedSessionDate(sessionId, scheduledDate, startTime, orderInDay),
    onSuccess: () => {
      invalidateCalendarQueries(queryClient);
    },
  });
}

/**
 * Hook for updating a session's status (completed, skipped, deleted).
 * Automatically invalidates calendar queries on success.
 * Returns the response which may be PROPOSAL_ONLY or the updated session.
 */
export function useUpdateSessionStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      sessionId,
      status,
      completedActivityId,
      confirmed,
    }: {
      sessionId: string;
      status: 'completed' | 'skipped' | 'deleted' | 'planned';
      completedActivityId?: string;
      confirmed?: boolean;
    }) => updateSessionStatus(sessionId, status, completedActivityId, confirmed),
    onSuccess: (response) => {
      // Only invalidate if not PROPOSAL_ONLY (which requires confirmation)
      // If it's PROPOSAL_ONLY, the caller will handle confirmation and call invalidateCalendar
      if (response && typeof response === 'object' && 'status' in response) {
        const writeResponse = response as WriteResponse<CalendarSession>;
        if (writeResponse.status === 'ok') {
          invalidateCalendarQueries(queryClient);
        }
        // If status is 'PROPOSAL_ONLY', don't invalidate yet - wait for confirmation
      } else {
        // Direct CalendarSession response (not wrapped in WriteResponse)
        invalidateCalendarQueries(queryClient);
      }
    },
  });
}

/**
 * PHASE F5: Hook for updating a workout's scheduled date via drag & drop.
 * Updates workout date via PATCH /workouts/{id}.
 * Backend handles session reorder and conflict resolution.
 * Frontend just re-fetches.
 */
export function useUpdateWorkoutDate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      workoutId,
      scheduledDate,
    }: {
      workoutId: string;
      scheduledDate: string;
    }) => updateWorkoutDate(workoutId, scheduledDate),
    onSuccess: () => {
      invalidateCalendarQueries(queryClient);
    },
  });
}
